import * as React from "react";
import { cn } from "@/lib/utils";
import { AlertTriangle, CheckCircle2, Info, ShieldAlert } from "lucide-react";

export type StatusTone = "neutral" | "info" | "success" | "warning" | "alert" | "live";

const toneClass: Record<StatusTone, string> = {
  neutral: "border-border bg-surface text-muted-foreground",
  info: "border-primary/30 bg-primary/10 text-primary",
  success: "border-success/30 bg-success/10 text-success",
  warning: "border-warning/40 bg-warning/10 text-warning",
  alert: "border-destructive/40 bg-destructive/10 text-destructive",
  live: "border-destructive/50 bg-destructive/10 text-destructive",
};

/** Compact status chip. `alert`/`live` use emergency red — clinical urgency only. */
export const StatusBadge = ({
  tone = "neutral",
  children,
  className,
}: {
  tone?: StatusTone;
  children: React.ReactNode;
  className?: string;
}) => (
  <span
    className={cn(
      "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold tracking-tight",
      toneClass[tone],
      className,
    )}
  >
    {tone === "live" ? (
      <span aria-hidden className="relative flex size-2">
        <span className="absolute inline-flex size-full animate-ping rounded-full bg-destructive/60" />
        <span className="relative inline-flex size-2 rounded-full bg-destructive" />
      </span>
    ) : null}
    {children}
  </span>
);

const noticeTone = {
  info: { cls: "border-primary/25 bg-primary/[0.06] text-foreground", Icon: Info, iconCls: "text-primary" },
  caution: { cls: "border-warning/35 bg-warning/[0.08] text-foreground", Icon: AlertTriangle, iconCls: "text-warning" },
  regulatory: { cls: "border-border bg-surface text-foreground", Icon: ShieldAlert, iconCls: "text-signal" },
  success: { cls: "border-success/30 bg-success/[0.08] text-foreground", Icon: CheckCircle2, iconCls: "text-success" },
} as const;

/** Regulatory / compliance notice block. */
export const RegulatoryNotice = ({
  tone = "regulatory",
  title,
  children,
  className,
}: {
  tone?: keyof typeof noticeTone;
  title?: string;
  children: React.ReactNode;
  className?: string;
}) => {
  const { cls, Icon, iconCls } = noticeTone[tone];
  return (
    <aside role="note" className={cn("flex gap-3 rounded-xl border p-4 sm:p-5", cls, className)}>
      <Icon aria-hidden className={cn("mt-0.5 size-5 shrink-0", iconCls)} />
      <div>
        {title ? <p className="ds-h5 mb-1">{title}</p> : null}
        <div className="ds-legal">{children}</div>
      </div>
    </aside>
  );
};
